class WishlistStorage {
  static STORAGE_KEY = 'sirius-wishlist';

  static getItems() {
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (error) {
      return [];
    }
  }

  static saveItems(items) {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(items));
    publish(PUB_SUB_EVENTS.wishlistUpdate, { items });
  }

  static hasVariant(variantId) {
    return this.getItems().some((item) => String(item.variantId) === String(variantId));
  }

  static toggle(item) {
    const items = this.getItems();
    const index = items.findIndex((entry) => String(entry.variantId) === String(item.variantId));

    if (index >= 0) {
      items.splice(index, 1);
    } else {
      items.push(item);
    }

    this.saveItems(items);
    return index < 0;
  }

  static removeVariant(variantId) {
    const items = this.getItems().filter((entry) => String(entry.variantId) !== String(variantId));
    this.saveItems(items);
  }
}

class WishlistCount extends HTMLElement {
  connectedCallback() {
    this.updateCount();
    this.unsubscribe = subscribe(PUB_SUB_EVENTS.wishlistUpdate, () => this.updateCount());
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
  }

  updateCount() {
    const count = WishlistStorage.getItems().length;
    const bubble = this.querySelector('.wishlist-count-bubble');
    const countNode = bubble?.querySelector('[data-wishlist-count]');

    if (!bubble || !countNode) return;

    if (count > 0) {
      bubble.hidden = false;
      countNode.textContent = count < 100 ? count : '99+';
    } else {
      bubble.hidden = true;
      countNode.textContent = '0';
    }
  }
}

class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('.wishlist-button__toggle');
    this.label = this.querySelector('.wishlist-button__label');

    if (!this.button) return;

    this.button.addEventListener('click', this.onClick.bind(this));
    this.unsubscribe = subscribe(PUB_SUB_EVENTS.wishlistUpdate, () => this.syncState());
    this.variantUnsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.onVariantChange(event));

    this.syncState();
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
    if (this.variantUnsubscribe) this.variantUnsubscribe();
  }

  onVariantChange(event) {
    if (!this.dataset.sectionId || event.data.sectionId !== this.dataset.sectionId) return;

    const variant = event.data.variant;
    if (!variant) return;

    this.dataset.variantId = variant.id;
    this.dataset.variantTitle = variant.title;
    this.dataset.productPrice = this.formatMoney(variant.price);
    this.dataset.productCompareAtPrice =
      variant.compare_at_price && variant.compare_at_price > variant.price
        ? this.formatMoney(variant.compare_at_price)
        : '';

    this.syncState();
  }

  onClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const item = this.getItemFromDataset();
    const added = WishlistStorage.toggle(item);
    this.syncState(added);
  }

  getItemFromDataset() {
    return {
      productId: this.dataset.productId,
      variantId: this.dataset.variantId,
      handle: this.dataset.productHandle,
      title: this.dataset.productTitle,
      variantTitle: this.dataset.variantTitle || '',
      url: this.dataset.productUrl,
      image: this.dataset.productImage || '',
      price: this.dataset.productPrice || '',
      compareAtPrice: this.dataset.productCompareAtPrice || '',
    };
  }

  syncState(added) {
    const isActive = WishlistStorage.hasVariant(this.dataset.variantId);
    this.classList.toggle('is-active', isActive);
    this.button.setAttribute('aria-pressed', isActive ? 'true' : 'false');

    if (this.label) {
      this.label.textContent = isActive
        ? window.wishlistStrings?.remove || 'Remove from wishlist'
        : window.wishlistStrings?.add || 'Add to wishlist';
    }

    if (typeof added === 'boolean') {
      const message = added
        ? window.wishlistStrings?.added || 'Added to wishlist'
        : window.wishlistStrings?.removed || 'Removed from wishlist';
      this.announce(message);
    }
  }

  announce(message) {
    let liveRegion = document.getElementById('wishlist-live-region');
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = 'wishlist-live-region';
      liveRegion.className = 'visually-hidden';
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      document.body.appendChild(liveRegion);
    }

    liveRegion.textContent = message;
  }

  formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }

    return `$${(cents / 100).toFixed(2)}`;
  }
}

class WishlistPage extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-wishlist-grid]');
    this.empty = this.querySelector('[data-wishlist-empty]');
    this.render();
    this.unsubscribe = subscribe(PUB_SUB_EVENTS.wishlistUpdate, () => this.render());
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
  }

  render() {
    const items = WishlistStorage.getItems();

    if (!this.grid || !this.empty) return;

    if (!items.length) {
      this.grid.innerHTML = '';
      this.empty.hidden = false;
      return;
    }

    this.empty.hidden = true;
    this.grid.innerHTML = items
      .map((item) => {
        const compareAt = item.compareAtPrice
          ? `<s class="price-item price-item--regular">${item.compareAtPrice}</s>`
          : '';
        const variantLabel =
          item.variantTitle && item.variantTitle !== 'Default Title'
            ? `<p class="caption">${item.variantTitle}</p>`
            : '';

        return `
          <article class="wishlist-page__item" data-variant-id="${item.variantId}">
            <wishlist-button
              class="wishlist-button wishlist-button--card is-active"
              data-product-id="${item.productId}"
              data-variant-id="${item.variantId}"
              data-product-handle="${item.handle}"
              data-product-title="${this.escapeHtml(item.title)}"
              data-variant-title="${this.escapeHtml(item.variantTitle || '')}"
              data-product-url="${item.url}"
              data-product-image="${item.image}"
              data-product-price="${item.price}"
              data-product-compare-at-price="${item.compareAtPrice || ''}"
            >
              <button type="button" class="wishlist-button__toggle" aria-pressed="true">
                <span class="svg-wrapper wishlist-button__icon">
                  <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-heart" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 5.24 8.515 3.773a4.433 4.433 0 0 0-6.21 0 4.293 4.293 0 0 0 0 6.128L10 17.495l7.695-7.593a4.293 4.293 0 0 0 0-6.128 4.433 4.433 0 0 0-6.21 0zm.765-2.177c2.113-2.084 5.538-2.084 7.65 0a5.29 5.29 0 0 1 0 7.55l-7.695 7.593a1.03 1.03 0 0 1-1.44 0l-7.696-7.594a5.29 5.29 0 0 1 0-7.549C3.697.98 7.122.98 9.234 3.063l.766.755z"/></svg>
                </span>
              </button>
            </wishlist-button>
            <a class="wishlist-page__media" href="${item.url}">
              ${
                item.image
                  ? `<img src="${item.image}" alt="${this.escapeHtml(item.title)}" loading="lazy" width="533" height="533">`
                  : ''
              }
            </a>
            <h3 class="wishlist-page__title"><a href="${item.url}">${this.escapeHtml(item.title)}</a></h3>
            ${variantLabel}
            <p class="wishlist-page__price price">
              ${compareAt}
              <span class="price-item price-item--sale">${item.price}</span>
            </p>
          </article>
        `;
      })
      .join('');

  }

  escapeHtml(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }
}

customElements.define('wishlist-count', WishlistCount);
customElements.define('wishlist-button', WishlistButton);
customElements.define('wishlist-page', WishlistPage);

document.addEventListener('DOMContentLoaded', () => {
  publish(PUB_SUB_EVENTS.wishlistUpdate, { items: WishlistStorage.getItems() });
});
